import Cookies from "js-cookie";

const COOKIE_USER_ID = "USER_ID";
const CACHE_PREFIX = "shp:apiCache:";
const META_SUFFIX = ":__meta__";
const MAX_ENTRIES = 80;

interface CacheEntryMeta {
    endpoint: string;
    savedAt: number;
}

interface CacheMetaFile {
    entries: CacheEntryMeta[];
}

interface StoredPayload {
    data: unknown;
    savedAt: number;
}

function storageKeyFor(userId: string, endpoint: string): string {
    return `${CACHE_PREFIX}${userId}:${encodeURIComponent(endpoint)}`;
}

function metaKeyFor(userId: string): string {
    return `${CACHE_PREFIX}${userId}${META_SUFFIX}`;
}

function getCurrentUserId(): string {
    return Cookies.get(COOKIE_USER_ID)?.trim() || "anon";
}

function readMeta(userId: string): CacheMetaFile {
    try {
        const raw = localStorage.getItem(metaKeyFor(userId));
        if (!raw) return { entries: [] };
        const parsed = JSON.parse(raw) as CacheMetaFile;
        if (!parsed.entries || !Array.isArray(parsed.entries)) return { entries: [] };
        return parsed;
    } catch {
        return { entries: [] };
    }
}

function writeMeta(userId: string, meta: CacheMetaFile): void {
    try {
        localStorage.setItem(metaKeyFor(userId), JSON.stringify(meta));
    } catch {
        /* quota or private mode */
    }
}

function removeStorageKey(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        /* ignore */
    }
}

function trimOldest(userId: string, meta: CacheMetaFile): void {
    while (meta.entries.length > MAX_ENTRIES) {
        const victim = meta.entries.shift();
        if (!victim) break;
        removeStorageKey(storageKeyFor(userId, victim.endpoint));
    }
}

export const responseCache = {
    getCurrentUserId,

    get<T>(endpoint: string, userId = getCurrentUserId()): T | null {
        try {
            const raw = localStorage.getItem(storageKeyFor(userId, endpoint));
            if (!raw) return null;
            const parsed = JSON.parse(raw) as StoredPayload;
            return parsed.data as T;
        } catch {
            return null;
        }
    },

    set(endpoint: string, data: unknown, userId = getCurrentUserId()): void {
        const now = Date.now();
        const key = storageKeyFor(userId, endpoint);
        const meta = readMeta(userId);

        const idx = meta.entries.findIndex((e) => e.endpoint === endpoint);
        if (idx >= 0) {
            meta.entries.splice(idx, 1);
        }
        meta.entries.push({ endpoint, savedAt: now });
        trimOldest(userId, meta);

        try {
            localStorage.setItem(key, JSON.stringify({ data, savedAt: now }));
            writeMeta(userId, meta);
        } catch {
            /* quota full: drop oldest until set succeeds */
            while (meta.entries.length > 1) {
                const oldest = meta.entries.shift();
                if (!oldest) break;
                removeStorageKey(storageKeyFor(userId, oldest.endpoint));
                try {
                    localStorage.setItem(key, JSON.stringify({ data, savedAt: now }));
                    writeMeta(userId, meta);
                    return;
                } catch {
                    /* continue shrinking */
                }
            }
        }
    },

    /** Call before clearing user session cookies so we know which user to wipe. */
    clearForUser(userId: string): void {
        if (!userId) return;
        const meta = readMeta(userId);
        for (const { endpoint } of meta.entries) {
            removeStorageKey(storageKeyFor(userId, endpoint));
        }
        removeStorageKey(metaKeyFor(userId));
    },

    removeMatching(prefix: string, userId = getCurrentUserId()): void {
        const meta = readMeta(userId);
        const retained = meta.entries.filter(({ endpoint }) => {
            if (!endpoint.startsWith(prefix)) return true;
            removeStorageKey(storageKeyFor(userId, endpoint));
            return false;
        });
        writeMeta(userId, { entries: retained });
    },
};
